import { UsersActionsTypes as types } from './actions';

const initialState = {
  all: { data: [], count: 0, loading: true },
  count: null,
  selection: {
    user: null,
    riderCashFlow: null,
    creditLog: null,
    userNotes: null,
    driverCashFlow: null,
    driverBalance: null,
    driverBalanceError: false,
    driverPaymentLog: null,
    driverPaymentLogError: false,
    paymentSources: null,
    sessions: null,
    driverScore: null,
    rideHistory: null,
    driveHistory: null,
    promotions: null,
    availablePromotions: null,
    referrals: null,
    referralsError: false,
    // Highest getReferrals requestId applied so far; used to reject a
    // same-user response that resolves out of order (see actions.js).
    referralsRequestId: 0,
  },
  averageDriverScores: null,
  isPending: false,
  currentLocations: {},
  error: null,
};

function reducer(state = initialState, action) {
  switch (action.type) {
    case types.FETCH_USERS_SUCCESS:
      return {
        ...state,
        all: action.payload,
        isPending: false,
      };
    case types.FETCH_USERS_STARTED:
      return {
        ...state,
        all: { ...state.all, loading: true },
        isPending: true,
      };
    case types.FETCH_USERS_FAILURE:
      return {
        ...state,
        error: action.payload,
        isPending: false,
      };
    case types.FETCH_CURRENT_LOCATION_SUCCESS:
      return {
        ...state,
        currentLocations: {
          ...state.currentLocations,
          [action.payload.userId]: action.payload.location,
        },
      };
    case types.FETCH_CURRENT_LOCATION_FAILURE:
      return {
        ...state,
        currentLocations: {
          ...state.currentLocations,
          [action.payload.userId]: null,
        },
      };
    case types.FETCH_USERS_COUNT_SUCCESS:
      return {
        ...state,
        count: action.payload,
        isPending: false,
      };
    case types.FETCH_USER_BYID_STARTED:
      return {
        ...state,
        selection: {},
      };
    case types.FETCH_USER_BYID_SUCCESS:
      return {
        ...state,
        selection: { user: action.payload },
      };
    case types.RELOAD_USER_SUCCESS:
      return {
        ...state,
        selection: { ...state.selection, user: action.payload },
      };
    case types.EDIT_USER_BYID_SUCCESS:
      return {
        ...state,
        selection: { ...state.selection, user: action.payload },
      };
    case types.UPDATE_USER_IMAGE_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          user: { ...state.selection.user, imageUrl: action.payload.imageUrl },
        },
      };
    case types.FETCH_RIDER_CASH_FLOW_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          riderCashFlow: action.payload || {},
        },
      };
    case types.FETCH_CREDIT_LOG_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          creditLog: action.payload || { data: [], count: 0 },
        },
      };
    case types.FETCH_USER_NOTES_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          userNotes: action.payload || [],
        },
      };
    case types.FETCH_USER_NOTES_FAILURE:
      return {
        ...state,
        selection: {
          ...state.selection,
          // Keep notes already loaded; only show the empty state on initial load.
          userNotes: state.selection.userNotes || [],
        },
      };
    case types.FETCH_DRIVER_CASH_FLOW_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          driverCashFlow: action.payload || {},
        },
      };
    case types.FETCH_DRIVER_BALANCE_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          driverBalance: action.payload || {},
          driverBalanceError: false,
        },
      };
    case types.FETCH_DRIVER_BALANCE_FAILURE:
      return {
        ...state,
        selection: {
          ...state.selection,
          driverBalanceError: true,
        },
      };
    case types.FETCH_DRIVER_PAYMENT_LOG_SUCCESS: {
      const { entries = [], nextCursor = null, append = false } = action.payload || {};
      const existing =
        append && state.selection.driverPaymentLog ? state.selection.driverPaymentLog.entries : [];
      return {
        ...state,
        selection: {
          ...state.selection,
          driverPaymentLog: { entries: [...existing, ...entries], nextCursor },
          driverPaymentLogError: false,
        },
      };
    }
    case types.FETCH_DRIVER_PAYMENT_LOG_FAILURE:
      return {
        ...state,
        selection: {
          ...state.selection,
          // Keep an already-loaded log; only surface the error on initial load.
          driverPaymentLog: state.selection.driverPaymentLog || { entries: [], nextCursor: null },
          driverPaymentLogError: true,
        },
      };
    case types.FETCH_PAYMENT_SOURCES_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          paymentSources: action.payload || [],
        },
      };
    case types.FETCH_SESSIONS_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          sessions: action.payload || [],
        },
      };
    case types.FETCH_DRIVER_SCORE_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          driverScore: action.payload,
        },
      };
    case types.FETCH_AVERAGE_DRIVER_SCORES_SUCCESS:
      return {
        ...state,
        averageDriverScores: action.payload,
      };
    case types.FETCH_RIDE_HISTORY_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          rideHistory: action.payload || [],
        },
      };
    case types.REFUND_RIDE_SUCCESS:
      const ride = action.payload;
      const rideId = ride.id || ride.rideId;
      return {
        ...state,
        selection: {
          ...state.selection,
          rideHistory: {
            ...state.selection.rideHistory,
            data: state.selection.rideHistory.data.map((rideHistoryItem) => {
              const rideHistoryItemId = rideHistoryItem.id || rideHistoryItem.rideId;
              if (rideHistoryItemId === rideId) {
                return { ...rideHistoryItem, ...ride };
              }
              return rideHistoryItem;
            }),
          },
        },
      };
    case types.FETCH_DRIVE_HISTORY_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          driveHistory: action.payload || [],
        },
      };
    case types.FETCH_PROMOTIONS_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          promotions: action.payload || [],
        },
      };
    case types.FETCH_AVAILABLE_PROMOTIONS_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          availablePromotions: action.payload || [],
        },
      };
    case types.FETCH_REFERRALS_SUCCESS: {
      // Ignore a response that landed after the operator navigated to a
      // different user, so one user's referrals can't bleed into another's.
      if (
        state.selection.user &&
        String(state.selection.user.id) !== String(action.payload.userId)
      ) {
        return state;
      }
      // The userId check above only rejects the *wrong* user; it can't tell
      // two in-flight requests for the *same* user apart. Re-visiting a user
      // fires a second request while an earlier one may still be in flight,
      // with no guarantee it resolves first — so also reject a response
      // whose requestId is older than the latest one already applied.
      if (action.payload.requestId < (state.selection.referralsRequestId || 0)) {
        return state;
      }
      // Guard against a malformed backend response: `|| []` only catches
      // falsy payloads, not a truthy non-array (e.g. an error-shaped `{}`
      // body), which would otherwise reach ReferralsCard's unconditional
      // `.filter(...)` calls and crash the whole page. Also drop any null
      // element so a partially-malformed array can't crash per-row access.
      const referrals = Array.isArray(action.payload.referrals)
        ? action.payload.referrals.filter(Boolean)
        : [];
      return {
        ...state,
        selection: {
          ...state.selection,
          referrals,
          referralsError: false,
          referralsRequestId: action.payload.requestId,
        },
      };
    }
    case types.FETCH_REFERRALS_FAILURE: {
      if (
        state.selection.user &&
        String(state.selection.user.id) !== String(action.payload.userId)
      ) {
        return state;
      }
      if (action.payload.requestId < (state.selection.referralsRequestId || 0)) {
        return state;
      }
      return {
        ...state,
        selection: {
          ...state.selection,
          referralsRequestId: action.payload.requestId,
          // Flag the error rather than coercing to an empty list — the card
          // must show a distinct error state, not a false "no referrals".
          referralsError: true,
        },
      };
    }
    case types.FETCH_BLACKLISTED_DATES_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          user: {
            ...state.selection.user,
            blacklistedDates: action.payload || [],
          },
        },
      };
    case types.FETCH_BLACKLISTED_TIMES_SUCCESS:
      return {
        ...state,
        selection: {
          ...state.selection,
          user: {
            ...state.selection.user,
            blacklistedTimes: action.payload || [],
          },
        },
      };
    case types.ADD_CREDITS_SUCCESS: {
      return {
        ...state,
        selection: {
          ...state.selection,
          user: { ...state.selection.user, ...action.payload },
        },
      };
    }

    // RESET reducers
    case types.RESET_ERROR:
      return {
        ...state,
        error: null,
      };
    case types.RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

export default reducer;
